import {
    Component,
    ojs,
    app,
    state,
    EventData,
    eData,
    v,
} from "modular-openscriptjs";
import { dom, tool } from "../../fotastart";
import { oneOf, either, each, cleanDispose, isMobile } from "../../helpers";

const h = app("h");
const broker = app("broker");
const $e = app("$e");

export default class ReviewForm extends Component {
    rating = state(0);
    holder = null;

    async showForm(ed, event) {
        let data = EventData.parse(ed);
        const review = data?.message.get("review");
        const orderId = data?.message.get("orderId", 0);

        this.rating.value = oneOf(review?.rating, 0);

        if (this.holder) {
            cleanDispose(this.holder);
        }

        if (isMobile()) {
            this.holder = tool.offcanvas(
                [
                    h.ReviewCanvas(this.renderForm(review)),
                    h.div(
                        {
                            class: "offcanvas-footer p-3",
                            style: "flex-shrink: 0;",
                        },
                        this.renderFooter(review, orderId),
                    ),
                ],
                { backdrop: true },
            );
        } else {
            this.holder = tool.plainModal(
                h.ReviewModal(
                    this.renderForm(review),
                    this.renderFooter(review, orderId),
                ),
                { backdrop: true },
            );
        }
        this.holder.show();
    }

    closeForm() {
        if (this.holder) {
            this.holder.hide();
        }
    }

    rate(rating) {
        this.rating.value = rating;
    }

    submitReview(reviewId, orderId) {
        let btn = dom.id("confirm-review-btn");
        dom.disable(btn);
        dom.put("Saving...", btn);

        broker.send(
            $e.review.needs.storage,
            eData(
                {},
                {
                    id: reviewId,
                    title: dom.id("review-input-title")?.value || "",
                    rating: this.rating.value,
                    review: dom.id("review-input-review")?.value || "",
                    orderId,
                },
            ),
        );
    }

    renderForm(review) {
        return h.div(
            { class: "w-100 text-start" },
            // Header
            h.div(
                {
                    class: "d-flex justify-content-between align-items-start mb-4",
                },
                h.div(
                    h.h4({ class: "fw-bold text-dark mb-1" }, "Review Delivery"),
                    // Make it unmistakable that this rates the deliverer/
                    // delivery service, not the businesses in the order -
                    // those get rated separately.
                    h.span(
                        {
                            class: "badge bg-primary-subtle text-primary rounded-pill fw-semibold",
                            style: "font-size: 0.7rem;",
                        },
                        h.i({ class: "fas fa-truck me-1" }),
                        "Delivery only — not the businesses",
                    ),
                ),
                h.button({
                    class: "btn-close bg-light rounded-circle p-2 flex-shrink-0",
                    onclick: this.method("closeForm"),
                    aria_label: "Close",
                }),
            ),

            // Hidden Review ID
            h.input({
                type: "hidden",
                id: "review-input-id",
                value: oneOf(review?.id, 0),
            }),

            // Stars
            h.label(
                { class: "form-label fw-bold small text-secondary mb-2" },
                "Your Rating",
            ),
            v(this.rating, (rating) => {
                return h.div(
                    { class: "d-flex gap-2 mb-4" },
                    each([1, 2, 3, 4, 5], (r) =>
                        h.span(
                            {
                                class: "fs-2 cursor-pointer transition-all",
                                onclick: this.method("rate", r),
                                style: "cursor: pointer;",
                            },
                            h.i({
                                class: either(
                                    r <= rating.value,
                                    "fas fa-star text-warning",
                                    "far fa-star text-secondary-subtle",
                                ),
                            }),
                        ),
                    ),
                );
            }),

            // Review Input
            h.div(
                { class: "mb-3" },
                h.label(
                    { class: "form-label fw-bold small text-secondary" },
                    "Comments",
                ),
                h.textarea(
                    {
                        class: "form-control bg-light border-0 p-3 rounded-3",
                        placeholder: "Share your experience...",
                        id: "review-input-review",
                        rows: "4",
                    },
                    oneOf(review?.review, ""),
                ),
            ),

            // Hidden Title Input
            h.input({
                type: "hidden",
                id: "review-input-title",
                value: oneOf(review?.title, ""),
            }),
        );
    }

    renderFooter(review, orderId) {
        let reviewId = oneOf(review?.id, 0);
        return h.div(
            {
                class: "d-flex justify-content-between align-items-center gap-3 w-100",
            },
            h.button(
                {
                    class: "btn btn-light rounded-pill px-4 py-3 fw-bold flex-fill",
                    onclick: this.method("closeForm"),
                },
                "Cancel",
            ),
            h.button(
                {
                    class: "btn btn-primary rounded-pill px-4 py-3 fw-bold shadow-sm d-flex align-items-center justify-content-center gap-2 flex-fill",
                    id: "confirm-review-btn",
                    onclick: this.method("submitReview", [reviewId, orderId]),
                },
                h.span("Submit"),
                h.i({ class: "fas fa-paper-plane" }),
            ),
        );
    }

    $$review = {
        stored: (ed, event) => {
            if (this.holder) {
                this.holder.hide();
            }
        },

        needs: {
            reviewForm: (ed, event) => this.showForm(ed, event),
        },

        has: {
            storageError: (ed, event) => {
                let btn = dom.id("confirm-review-btn");
                if (btn) {
                    dom.enable(btn);
                    dom.put(h.span("Submit"), btn);
                    dom.add(h.i({ class: "fas fa-paper-plane" }), btn);
                }
            },
        },
    };

    cleanUp() {
        super.cleanUp();
        if (this.holder) cleanDispose(this.holder);
    }
}

export function ReviewCanvas(bodyContent) {
    return h.$(
        h.div(
            {
                class: "offcanvas-body p-4",
                style: "flex-grow: 1; overflow-y: auto;",
            },
            bodyContent,
        ),
    );
}

export function ReviewModal(bodyContent, footerContent) {
    return h.$(
        h.div(
            {
                class: "modal-content border-0 rounded-4 shadow-lg",
            },
            h.div(
                {
                    class: "modal-body p-4",
                },
                bodyContent,
                h.div(
                    {
                        class: "modal-footer p-4",
                    },
                    footerContent,
                ),
            ),
        ),
    );
}

ojs(ReviewForm, ReviewCanvas, ReviewModal);
